import React, { useRef, useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, db } from './config/firebase';
import { collection, addDoc } from 'firebase/firestore';
import Auth from './components/Auth';
// BUG FIX #3 : Header importé mais jamais utilisé → supprimé
import { Pose, POSE_CONNECTIONS } from '@mediapipe/pose';
import { Camera } from '@mediapipe/camera_utils';
import { drawConnectors, drawLandmarks } from '@mediapipe/drawing_utils';
import './App.css';

// --- UTILITAIRES ---
const calculateAngle = (a, b, c) => {
  const radians = Math.atan2(c.y - b.y, c.x - b.x) - Math.atan2(a.y - b.y, a.x - b.x);
  let angle = Math.abs(radians * 180.0 / Math.PI);
  if (angle > 180.0) angle = 360 - angle;
  return angle;
};

const speak = (text) => {
  window.speechSynthesis.cancel();
  const msg = new SpeechSynthesisUtterance(text);
  msg.lang = 'fr-FR';
  msg.rate = 1.1;
  window.speechSynthesis.speak(msg);
};

function App() {
  const [user, setUser] = useState(null);
  const [sessionPhase, setSessionPhase] = useState('idle'); // 'idle' | 'countdown' | 'exercise' | 'rest' | 'complete'
  const [isPaused, setIsPaused] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [reps, setReps] = useState(0);
  const [currentSeries, setCurrentSeries] = useState(1);
  const [currentPhase, setCurrentPhase] = useState('ready');
  const [partScores, setPartScores] = useState({ back: 100, legs: 100 });
  const [repHistory, setRepHistory] = useState([]);

  // Variables dérivées
  const isActive = sessionPhase !== 'idle' && sessionPhase !== 'complete';
  const isResting = sessionPhase === 'rest';
  const isInitialCountdown = sessionPhase === 'countdown';
  // Debug overlay : valeurs temps réel pour diagnostiquer la détection
  const [debugInfo, setDebugInfo] = useState({
    backAngle: 0, kneeAngle: 0, phase: 'ready',
    atBottom: false, squatFault: false, armRaised: false,
    isProfile: true
  });

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const tutorialVideoRef = useRef(null);
  const cameraRef = useRef(null);
  const poseRef = useRef(null);
  const phaseRef = useRef('ready');

  // 🔍 DEBUG AUTH : Début du composant App (commenté pour éviter le spam 30fps)
  // console.log('🔹 [App] Composant App monté, user initial:', user);
  const analysisEnabled = useRef(false);
  const baselineHipY = useRef(0);
  const scoresInRep = useRef([]);
  // BUG FIX #1 : ref pour reps → évite closure figée dans analyzeSwing
  const repsRef = useRef(0);
  // BUG FIX #4 : stocker les derniers landmarks pour baselineHipY au démarrage
  const lastLandmarksRef = useRef(null);
  // BUG FIX #2 : ref vers onResults pour éviter de recréer Pose à chaque render
  const onResultsRef = useRef(null);
  // nextSeries via ref → evite de l'ajouter dans les deps d'analyzeSwing (cycle infini)
  const nextSeriesRef = useRef(null);
  // Ref pour l'interval en cours → permet de l'annuler proprement avant d'en créer un nouveau
  const timerRef = useRef(null);
  // Ref pour manipuler le compte à rebours manuellement
  const countdownRef = useRef(0);
  // Ref pour la pause du chrono
  const isPausedRef = useRef(false);
  // Ref pour currentSeries → évite closure figée dans nextSeries
  const currentSeriesRef = useRef(1);
  // *** REF CRITIQUE : sessionPhaseRef toujours à jour, lisible dans les setInterval ***
  const sessionPhaseRef = useRef('idle');
  // Hysterèse : true quand le bas du swing a été atteint
  const wasAtBottomRef = useRef(false);
  // Filtres pour rep valide
  const squatFaultRef = useRef(false);
  const armRaisedRef = useRef(false);
  const headDownFaultRef = useRef(false); // Ref pour la tête baissée
  // Cooldown : timestamp de la dernière rep pour éviter le double-comptage
  const lastRepTimeRef = useRef(0);

  // Synchroniser sessionPhaseRef à chaque changement de sessionPhase
  useEffect(() => {
    sessionPhaseRef.current = sessionPhase;
  }, [sessionPhase]);

  // Synchroniser currentSeriesRef à chaque changement de currentSeries
  useEffect(() => {
    currentSeriesRef.current = currentSeries;
  }, [currentSeries]);

  // ==================== EXPORT TXT ====================
  const exportSessionTxt = () => {
    const totalScore = repHistory.length > 0 
      ? Math.round(repHistory.reduce((acc, curr) => acc + curr.score, 0) / repHistory.length) 
      : 0;
    
    const content = `RAPPORT DE SESSION KB FIT\n-------------------------\nDate: ${new Date().toLocaleString()}\nRépétitions validées: ${reps}\nScore de précision moyen: ${totalScore}%\n\nDétail des répétitions:\n${repHistory.map(r => `Rep #${r.id}: ${r.score}%`).join('\n')}`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kbfit-session-${Date.now()}.txt`;
    a.click();
  };

  // ==================== LOGIQUE DÉTECTION (hysterèse) ====================
  // IMPORTANT : toutes les gardes lisent des REFS, jamais des state closures,
  // car cette fonction est appelée depuis le pipeline Camera→Pose→onResults
  // qui ne passe PAS par les re-renders React.
  const analyzeSwing = useCallback((landmarks) => {
    // BUG FIX : On ne compte les répétitions QUE pendant l'exercice (bloque pendant 'countdown' et 'rest')
    if (!analysisEnabled.current || sessionPhaseRef.current !== 'exercise' || isPausedRef.current) return;

    // Choisir le côté le plus visible (side view)
    const useLeft = (landmarks[23].visibility ?? 1) >= (landmarks[24].visibility ?? 1);
    const shoulder = useLeft ? landmarks[11] : landmarks[12];
    const hip      = useLeft ? landmarks[23] : landmarks[24];
    const knee     = useLeft ? landmarks[25] : landmarks[26];
    const ankle    = useLeft ? landmarks[27] : landmarks[28];
    const wrist    = useLeft ? landmarks[15] : landmarks[16]; // Poignet pour les bras

    const kneeAngle = calculateAngle(hip, knee, ankle);
    const backAngle = calculateAngle(shoulder, hip, knee);

    // Détection de l'orientation (profil vs face)
    const shoulderDistX = Math.abs(landmarks[11].x - landmarks[12].x);
    const isProfile = shoulderDistX < 0.15;

    // Mise à jour debug overlay
    setDebugInfo({ 
      backAngle: Math.round(backAngle), kneeAngle: Math.round(kneeAngle), 
      phase: phaseRef.current, atBottom: wasAtBottomRef.current,
      squatFault: squatFaultRef.current, armRaised: armRaisedRef.current,
      headDownFault: headDownFaultRef.current,
      isProfile
    });

    // ─── DÉTECTION PAR HYSTERÈSE ────────────────────────────────────────
    let bottomCondition = false;
    let topCondition = false;
    let isValidBottom = false;

    if (isProfile) {
      // Logique VUE DE PROFIL (Basée sur l'angle du dos)
      bottomCondition = backAngle < 145 && !wasAtBottomRef.current;
      isValidBottom = kneeAngle > 115 && backAngle < 140;
      topCondition = backAngle > 162 && wasAtBottomRef.current;
      
      if (wasAtBottomRef.current) {
        if (kneeAngle < 115) squatFaultRef.current = true;
        if (backAngle > 140 && phaseRef.current === 'bottom') squatFaultRef.current = true;
        if (wrist.y < hip.y) armRaisedRef.current = true;
        // Détection tête baissée : Le nez (0) tombe plus bas que les oreilles (7/8)
        if (landmarks[0].y > landmarks[7].y && landmarks[0].y > landmarks[8].y) {
          headDownFaultRef.current = true;
        }
      }
    } else {
      // Logique VUE DE FACE (Basée sur la hauteur des poignets et l'écart des genoux)
      // En 2D de face, le poignet qui descend sous la hanche = phase basse
      bottomCondition = wrist.y > hip.y && !wasAtBottomRef.current;
      isValidBottom = true; // Toujours valide pour entrer en phase basse de face
      
      // Le poignet remonte au-dessus de la taille = phase haute
      topCondition = wrist.y < hip.y && wasAtBottomRef.current;

      if (wasAtBottomRef.current) {
        // Détection de Valgus (genoux qui rentrent) au lieu du dos courbé
        const kneeDistX = Math.abs(landmarks[25].x - landmarks[26].x);
        const ankleDistX = Math.abs(landmarks[27].x - landmarks[28].x);
        const kneeCollapse = kneeDistX < (ankleDistX * 0.75); // Tolérance de 75%
        
        if (kneeCollapse || kneeAngle < 115) squatFaultRef.current = true;
        if (wrist.y < hip.y) armRaisedRef.current = true;
        // Détection tête baissée de face
        if (landmarks[0].y > landmarks[7].y && landmarks[0].y > landmarks[8].y) {
          headDownFaultRef.current = true;
        }
      }
    }

    if (bottomCondition) {
      if (isValidBottom) {
        wasAtBottomRef.current = true;
        squatFaultRef.current = false;
        armRaisedRef.current = false;
        headDownFaultRef.current = false;
        phaseRef.current = 'bottom';
        setCurrentPhase('bottom');
        scoresInRep.current = [];
      }
    }

    if (topCondition) {
      const now = Date.now();
      if (now - lastRepTimeRef.current > 800) {
        wasAtBottomRef.current = false;
        lastRepTimeRef.current = now;

        // VALIDATION DE LA REP: pas de squat ET poignets montés ET tête pas baissée
        if (!squatFaultRef.current && armRaisedRef.current && !headDownFaultRef.current) {
          const avgScore = scoresInRep.current.length > 0
            ? Math.round(scoresInRep.current.reduce((a, b) => a + b) / scoresInRep.current.length)
            : 100;
          phaseRef.current = 'top';
          setCurrentPhase('top');
          repsRef.current = repsRef.current + 1;
          setReps(repsRef.current);
          setRepHistory(prev => [...prev, { id: prev.length + 1, score: avgScore }]);
          
          // Annonce vocale de la répétition
          speak(String(repsRef.current));

          // DÉCLENCHEMENT AUTOMATIQUE À 10 REPS
          if (repsRef.current >= 10) {
            nextSeriesRef.current?.();
          }
        } else {
          // REP INVALIDÉE : Explication vocale
          if (headDownFaultRef.current) {
            speak("Regarde devant toi");
          } else if (squatFaultRef.current) {
            speak("Attention, mouvement de squat");
          } else if (!armRaisedRef.current) {
            speak("Lève plus les bras");
          } else {
            speak("Mouvement incorrect");
          }
        }
      } else {
        wasAtBottomRef.current = false;
      }
    }

    // Phase intermédiaire pour l'affichage uniquement
    if (!wasAtBottomRef.current && backAngle < 145 && phaseRef.current === 'top') {
      phaseRef.current = 'ready';
      setCurrentPhase('ready');
    }
    if (wasAtBottomRef.current && backAngle > 125 && phaseRef.current === 'bottom') {
      phaseRef.current = 'ascending';
      setCurrentPhase('ascending');
    }

    // Scoring phase-aware (couleur squelette)
    const isTopPhase = phaseRef.current === 'top' || phaseRef.current === 'ready';
    const isBottomPhase = phaseRef.current === 'bottom' || phaseRef.current === 'ascending';

    let goodBack = false;
    if (isTopPhase) {
      goodBack = backAngle > 150;
    } else if (isBottomPhase) {
      goodBack = backAngle < 140 && kneeAngle > 115;
    }

    const goodKnee = kneeAngle > 115;
    setPartScores({ back: goodBack ? 100 : 50, legs: goodKnee ? 100 : 50 });
    scoresInRep.current.push((goodBack ? 50 : 0) + (goodKnee ? 50 : 0));

  }, []); // ← AUCUNE dépendance state : tout est lu via refs

  const onResults = useCallback((results) => {
    if (!canvasRef.current || !results.poseLandmarks) return;
    // BUG FIX #4 : stocker les landmarks pour pouvoir calculer la baseline au démarrage
    lastLandmarksRef.current = results.poseLandmarks;
    const ctx = canvasRef.current.getContext('2d');
    canvasRef.current.width = videoRef.current.videoWidth;
    canvasRef.current.height = videoRef.current.videoHeight;
    ctx.save();
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

    const color = partScores.back === 100 ? '#10B981' : '#EF4444';
    drawConnectors(ctx, results.poseLandmarks, POSE_CONNECTIONS, { color, lineWidth: 4 });
    drawLandmarks(ctx, results.poseLandmarks, { color: '#FFF', radius: 2 });
    ctx.restore();

    analyzeSwing(results.poseLandmarks);
  }, [partScores]);

  // BUG FIX #2 : garder onResultsRef à jour
  useEffect(() => {
    onResultsRef.current = onResults;
  }, [onResults]);

  // Garder nextSeriesRef à jour (évite de l'inclure dans les deps d'analyzeSwing)
  useEffect(() => {
    nextSeriesRef.current = nextSeries;
  });

  // ==================== GESTION DES SÉRIES & PAUSE ====================
  const startSession = async () => {
    // Réinitialisation complète pour une nouvelle session
    setCurrentSeries(1);
    currentSeriesRef.current = 1;
    setRepHistory([]);
    setReps(0);
    repsRef.current = 0;
    scoresInRep.current = [];
    
    const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 1280, height: 720 } });
    videoRef.current.srcObject = stream;
    await videoRef.current.play();
    
    const camera = new Camera(videoRef.current, {
      onFrame: async () => { 
        if (poseRef.current && !isPaused) await poseRef.current.send({ image: videoRef.current }); 
      }
    });
    camera.start();
    cameraRef.current = camera;
    setSessionPhase('countdown');
    setIsPaused(false);
    isPausedRef.current = false;
    runCycle(15);
    
    // Fait remonter tout en haut de la page pour que l'en-tête ne soit pas coupé
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  // NOTE: currentSeriesRef et sessionPhaseRef sont synchronisés via les useEffect ci-dessus

  const runCycle = (seconds) => {
    // Annuler tout interval précédent avant d'en créer un nouveau
    if (timerRef.current) clearInterval(timerRef.current);
    repsRef.current = 0; // Reset des reps pour la nouvelle série
    setReps(0);
    countdownRef.current = seconds;
    setCountdown(countdownRef.current);
    timerRef.current = setInterval(() => {
      if (isPausedRef.current) return; // Pause du chrono !
      countdownRef.current--;
      setCountdown(countdownRef.current);
      if (countdownRef.current === 3 && tutorialVideoRef.current) tutorialVideoRef.current.play();
      if (countdownRef.current <= 0) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        setCountdown(null);
        setSessionPhase('exercise');
        analysisEnabled.current = true;
        const landmarks = lastLandmarksRef.current;
        if (landmarks) baselineHipY.current = (landmarks[23].y + landmarks[24].y) / 2;
        speak('Allez !');
      }
    }, 1000);
  };

  const nextSeries = () => {
    const phase = sessionPhaseRef.current;
    const serie = currentSeriesRef.current;
    console.log('🟢 nextSeries() CALLED — phase:', phase, 'serie:', serie, 'timer:', !!timerRef.current, 'paused:', isPausedRef.current);

    // 1. Passer le décompte initial → démarrer l'exercice
    if (phase === 'countdown') {
      console.log('🟢 → Branch 1: countdown → exercise');
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setCountdown(null);
      sessionPhaseRef.current = 'exercise';
      setSessionPhase('exercise');
      repsRef.current = 0;
      setReps(0);
      wasAtBottomRef.current = false;
      analysisEnabled.current = true;
      if (tutorialVideoRef.current) tutorialVideoRef.current.play();
      speak('Allez !');
      return;
    }

    // 2. Passer le repos → démarrer la série suivante
    if (phase === 'rest') {
      console.log('🟢 → Branch 2: rest → exercise (skip rest)');
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setCountdown(null);
      sessionPhaseRef.current = 'exercise';
      setSessionPhase('exercise');
      currentSeriesRef.current += 1;
      setCurrentSeries(currentSeriesRef.current);
      repsRef.current = 0;
      setReps(0);
      wasAtBottomRef.current = false;
      const landmarks = lastLandmarksRef.current;
      if (landmarks) baselineHipY.current = (landmarks[23].y + landmarks[24].y) / 2;
      analysisEnabled.current = true;
      if (tutorialVideoRef.current) tutorialVideoRef.current.play();
      speak('Allez !');
      return;
    }

    // 3. Fin d'exercice → démarrer le repos (ou terminer si 5ème série)
    console.log('🟢 → Branch 3: exercise → rest (serie:', serie, '< 5 ?', serie < 5, ')');
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    analysisEnabled.current = false;
    if (tutorialVideoRef.current) {
      tutorialVideoRef.current.pause();
      tutorialVideoRef.current.currentTime = 0;
    }

    if (serie < 5) {
      sessionPhaseRef.current = 'rest';
      setSessionPhase('rest');
      speak('Repos 30 secondes');
      countdownRef.current = 30;
      setCountdown(countdownRef.current);
      console.log('🟢 → Rest timer CREATED, restCount:', countdownRef.current);
      timerRef.current = setInterval(() => {
        if (isPausedRef.current) return;
        countdownRef.current--;
        setCountdown(countdownRef.current);
        if (countdownRef.current === 3 && tutorialVideoRef.current) tutorialVideoRef.current.play();
        if (countdownRef.current <= 0) {
          console.log('🟢 → Rest timer DONE → exercise');
          clearInterval(timerRef.current);
          timerRef.current = null;
          setCountdown(null);
          sessionPhaseRef.current = 'exercise';
          setSessionPhase('exercise');
          currentSeriesRef.current += 1;
          setCurrentSeries(currentSeriesRef.current);
          repsRef.current = 0;
          setReps(0);
          wasAtBottomRef.current = false;
          const landmarks = lastLandmarksRef.current;
          if (landmarks) baselineHipY.current = (landmarks[23].y + landmarks[24].y) / 2;
          analysisEnabled.current = true;
          speak('Allez !');
        }
      }, 1000);
    } else {
      console.log('🟢 → Session complete (serie >= 5)');
      stopSession();
    }
  };



  const togglePause = () => {
    if (isPaused) {
      setIsPaused(false);
      isPausedRef.current = false;
      if (tutorialVideoRef.current) tutorialVideoRef.current.play();
      speak("Reprise");
    } else {
      setIsPaused(true);
      isPausedRef.current = true;
      if (tutorialVideoRef.current) tutorialVideoRef.current.pause();
      speak("Pause");
    }
  };

  const stopSession = async () => {
    analysisEnabled.current = false;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    setCountdown(null);
    if (cameraRef.current) cameraRef.current.stop();
    if (tutorialVideoRef.current) {
      tutorialVideoRef.current.pause();
      tutorialVideoRef.current.currentTime = 0;
    }
    setSessionPhase('complete');
    setIsPaused(false);
    isPausedRef.current = false;
    
    // Calcul et sauvegarde Firestore
    if (user) {
      if (repHistory.length > 0) {
        const avgScore = Math.round(repHistory.reduce((acc, curr) => acc + curr.score, 0) / repHistory.length);
        try {
          await addDoc(collection(db, 'users', user.uid, 'sessions'), {
            date: new Date().toISOString(),
            totalReps: repHistory.length,
            averageScore: avgScore,
            seriesCompleted: currentSeries,
            exerciseType: 'Kettlebell Swing'
          });
          console.log('Session sauvegardée dans Firestore !');
          speak("Données sauvegardées");
        } catch (e) {
          console.error('Erreur lors de la sauvegarde Firestore :', e);
          speak("Erreur de sauvegarde de la base de données");
        }
      } else {
        speak("Aucune répétition validée, sauvegarde annulée");
      }
    }

    exportSessionTxt();
    speak("Session terminée. Rapport exporté.");
  };

  // BUG FIX #2 : séparer Auth et Pose en deux useEffect distincts
  // Auth → une seule fois
  useEffect(() => {
    console.log('🔹 [Auth] Configuration du listener onAuthStateChanged');
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      console.log('🔹 [Auth] onAuthStateChanged déclenché:', firebaseUser);
      console.log('🔹 [Auth] firebaseUser.uid:', firebaseUser?.uid);
      console.log('🔹 [Auth] firebaseUser.email:', firebaseUser?.email);
      setUser(firebaseUser);
      console.log('🔹 [Auth] setUser appelé avec:', firebaseUser);
    });
    return () => {
      console.log('🔹 [Auth] Unsubscribe du listener');
      unsubscribe();
    };
  }, []);

  // BUG FIX #2 : Pose → initialisée une seule fois, jamais recréée
  // Elle appelle onResultsRef.current qui pointe toujours vers la version à jour
  useEffect(() => {
    const pose = new Pose({ locateFile: (f) => `https://cdn.jsdelivr.net/npm/@mediapipe/pose/${f}` });
    pose.setOptions({ modelComplexity: 1, smoothLandmarks: true, minDetectionConfidence: 0.6, minTrackingConfidence: 0.6 });
    pose.onResults((results) => {
      // Déléguer au ref → jamais besoin de recréer Pose
      if (onResultsRef.current) onResultsRef.current(results);
    });
    poseRef.current = pose;
    // Pas de cleanup ici : Pose n'a pas de méthode destroy standard
  }, []); // ← tableau vide : Pose créée une seule fois ✅

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      console.log('👋 Déconnexion réussie');
    } catch (error) {
      console.error('Erreur déconnexion:', error);
    }
  };

  // 🔍 DEBUG AUTH : Décision d'affichage
  // Logs commentés pour ne pas spammer la console à 30fps
  // console.log('🔹 [App] Render user:', user, 'type:', typeof user);
  // console.log('🔹 [App] !user =', !user, '→', !user ? 'Afficher Auth' : 'Afficher App principale');

  if (!user) return <Auth onAuthSuccess={setUser} />;

  return (
    <div className="App">
      <div className="header-bar">
        <div className="header-left" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div className="logo-header">
            <img src="/logo-kbfit.png" alt="Logo" className="logo" />
            <div><h1>KB FIT</h1><p className="subtitle">Rééducation Kettlebell</p></div>
          </div>
          <button
            onClick={handleLogout}
            style={{
              background: '#EF4444',
              color: 'white',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '0.9rem',
              alignSelf: 'flex-start'
            }}
          >
            🚪 Déconnexion
          </button>
        </div>
        
        {/* Affichage des reps uniquement sur mobile dans l'en-tête */}
        <div className="mobile-reps-header">
          <div className="mobile-series-badge">SÉRIE {currentSeries} / 5</div>
          <div style={{ fontSize: '1.1rem', color: '#C084FC', fontWeight: 'bold', marginTop: '5px' }}>Répétitions</div>
          <strong style={{ fontSize: '4.5rem', color: '#7C3AED', lineHeight: 1 }}>{reps}</strong>
        </div>
      </div>

      {/* Bouton Démarrer bien visible au centre avant les vidéos */}
      <div className="start-btn-container" style={{ textAlign: 'center', marginBottom: '15px', marginTop: '10px' }}>
        {!isActive && <button className="btn-start" onClick={startSession}>▶ DÉMARRER LA SESSION</button>}
      </div>

      <div className="content-grid">
        <div className="main-area">
          <div className="video-card">
            <div className="action-bar">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span className="current-phase">{currentPhase.toUpperCase()}</span>
                  {isActive && (
                    <span style={{
                      backgroundColor: debugInfo.isProfile ? 'rgba(16, 185, 129, 0.2)' : 'rgba(59, 130, 246, 0.2)',
                      border: `1px solid ${debugInfo.isProfile ? '#10B981' : '#3B82F6'}`,
                      color: debugInfo.isProfile ? '#10B981' : '#60A5FA',
                      padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold'
                    }}>
                      {debugInfo.isProfile ? 'VUE PROFIL' : 'VUE FACE'}
                    </span>
                  )}
                </div>
                {isActive && (
                  <div className="action-btns">
                    {/* Bouton manuel pour déclencher la série suivante OU passer le repos */}
                    <button className="btn-action" onClick={() => nextSeriesRef.current?.()}>
                      {isInitialCountdown ? '⏭ PASSER LE DÉCOMPTE' : (isResting ? '⏭ PASSER LE REPOS' : '⏭ SÉRIE SUIVANTE')}
                    </button>
                    <button className="btn-action" onClick={togglePause}>{isPaused ? '▶ REPRENDRE' : '⏸ PAUSE'}</button>
                    <button className="btn-action btn-danger" onClick={stopSession}>⏹ ARRÊTER</button>
                  </div>
                )}
              </div>
            <div className="video-wrapper">
              {/* Floutage automatique de la caméra pendant les 7 premières sec */}
              {/* BUG FIX #5 : guard null → floutage actif seulement pendant le compte à rebours initial */}
              <video 
                ref={videoRef} 
                autoPlay playsInline muted 
              />
              <canvas ref={canvasRef} />

              {/* Compte à rebours Haute Visibilité + curseur */}
              {countdown !== null && (
                <div className="countdown-screen" style={{ 
                  position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  background: (sessionPhase === 'countdown' && countdown > 3) ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.4)',
                  backdropFilter: (sessionPhase === 'countdown' && countdown > 3) ? 'blur(12px)' : 'none',
                  WebkitBackdropFilter: (sessionPhase === 'countdown' && countdown > 3) ? 'blur(12px)' : 'none',
                  zIndex: 100, gap: '20px'
                }}>
                  <div style={{ fontSize: '1.2rem', color: '#C084FC', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '2px' }}>
                    {sessionPhase === 'countdown' ? '⏱ PRÉPARATION' : sessionPhase === 'rest' ? '😮‍💨 REPOS' : '🏋️ EXERCICE'}
                  </div>
                  {sessionPhase === 'rest' ? (
                    <div style={{ position: 'relative', width: '240px', height: '240px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="240" height="240" style={{ position: 'absolute', top: 0, left: 0, transform: 'rotate(-90deg)' }}>
                        <circle cx="120" cy="120" r="100" fill="none" stroke="rgba(124, 58, 237, 0.2)" strokeWidth="16" />
                        <circle cx="120" cy="120" r="100" fill="none" stroke="#C084FC" strokeWidth="16" 
                          strokeDasharray={2 * Math.PI * 100} 
                          strokeDashoffset={((30 - countdown) / 30) * (2 * Math.PI * 100)} 
                          strokeLinecap="round" 
                          style={{ transition: 'stroke-dashoffset 1s linear' }} 
                        />
                      </svg>
                      <div style={{ 
                        fontSize: '7rem', color: 'white', fontWeight: '900', zIndex: 1,
                        textShadow: '0 0 20px rgba(124, 58, 237, 0.5)', lineHeight: 1
                      }}>
                        {countdown}
                      </div>
                    </div>
                  ) : (
                    <div style={{ 
                      fontSize: '12rem', color: 'white', fontWeight: '900', 
                      textShadow: '0 0 40px rgba(0,0,0,0.8), 0 0 20px #7C3AED', lineHeight: 1
                    }}>
                      {countdown}
                    </div>
                  )}
                  {/* Curseur de scrub pour avancer manuellement le décompte */}
                  <input 
                    type="range" 
                    min="0" 
                    max={sessionPhase === 'countdown' ? 15 : 30} 
                    value={(sessionPhase === 'countdown' ? 15 : 30) - countdown} 
                    onChange={(e) => {
                      const maxVal = sessionPhase === 'countdown' ? 15 : 30;
                      const progress = parseInt(e.target.value, 10);
                      const newCountdown = maxVal - progress;
                      countdownRef.current = newCountdown;
                      setCountdown(newCountdown);
                    }}
                    style={{
                      width: '60%',
                      cursor: 'pointer',
                      accentColor: '#7C3AED'
                    }}
                  />
                  <div style={{ fontSize: '0.9rem', color: '#E9D5FF', marginTop: '-10px' }}>
                    Glisser vers la droite pour avancer
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="main-area">

          <div className="video-card tutorial-card">
            <div className="action-bar"><span>SÉRIE {currentSeries} / 5</span></div>
            <div className="video-wrapper">
              <video 
                ref={tutorialVideoRef} 
                src="/kbswing10rep.mp4" 
                controls
                className="tutorial-video-full" 
              />
            </div>
          </div>
        </div>

        <div className="sidebar">
          <div className="info-card performance-card" style={{ padding: '30px', textAlign: 'center' }}>
            <h3 style={{ fontSize: '1.8rem' }}>📊 PERFORMANCE</h3>
            {isActive ? (
              <div style={{ margin: '20px 0' }}>
                <div style={{ fontSize: '1.5rem', color: '#C084FC' }}>Répétitions</div>
                <strong style={{ fontSize: '8rem', color: '#7C3AED', lineHeight: 1 }}>{reps}</strong>
              </div>
            ) : (
              <div style={{ color: '#94A3B8', marginTop: '20px' }}>En attente du démarrage...</div>
            )}
          </div>

          <div className="info-card analyse-side-card" style={{ padding: '30px' }}>
            <h3 style={{ fontSize: '1.8rem' }}>🛡️ ANALYSE SIDE</h3>
            {isActive ? (
              <>
                <div className="stat-line" style={{ margin: '20px 0' }}>
                  <span style={{ fontSize: '1.5rem' }}>Dos</span>
                  <span style={{ color: partScores.back === 100 ? '#10B981' : '#EF4444', fontSize: '2.5rem', fontWeight: 'bold' }}>
                    {partScores.back === 100 ? 'ALIGNE' : 'ALERTE'}
                  </span>
                </div>
                <div className="stat-line">
                  <span style={{ fontSize: '1.5rem' }}>Hanches</span>
                  <span style={{ color: partScores.legs === 100 ? '#10B981' : '#F59E0B', fontSize: '2.5rem', fontWeight: 'bold' }}>
                    {partScores.legs === 100 ? 'OK' : 'SQUAT'}
                  </span>
                </div>
              </>
            ) : (
              <div style={{ color: '#94A3B8', marginTop: '20px' }}>En attente du démarrage...</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
